import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCookieBite, faShieldHalved, faChartSimple, faBullhorn, faCheck, faTimes, faCog } from '@fortawesome/free-solid-svg-icons';

interface CookieBannerProps {
  className?: string;
}

const CookieBanner: React.FC<CookieBannerProps> = ({ className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Sempre attivo e non disattivabile
    analytics: false,
    marketing: false
  });
  const [showPreferences, setShowPreferences] = useState(false);

  // Controlla se i cookie sono già stati accettati
  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Mostra il banner dopo un breve ritardo per migliorare l'esperienza utente
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Carica le preferenze salvate
      try {
        const savedPreferences = JSON.parse(cookieConsent);
        setPreferences(savedPreferences);
      } catch (e) {
        console.error('Errore nel parsing delle preferenze cookie', e);
      }
    }
  }, []);

  // Accetta tutti i cookie
  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setIsVisible(false);
  };

  // Salva le preferenze personalizzate
  const savePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setIsVisible(false);
  };

  // Rifiuta tutti i cookie tranne quelli necessari
  const rejectAll = () => {
    const allRejected = {
      necessary: true, // I cookie necessari non possono essere rifiutati
      analytics: false,
      marketing: false
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allRejected));
    setPreferences(allRejected);
    setIsVisible(false);
  };

  // Gestisce il cambio di stato delle preferenze
  const handlePreferenceChange = (key: keyof typeof preferences) => {
    if (key === 'necessary') return; // Non permettere di disattivare i cookie necessari
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Varianti per le animazioni
  const bannerVariants = {
    hidden: { y: 100, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 300, damping: 30 } },
    exit: { y: 100, opacity: 0, transition: { duration: 0.3 } }
  };

  const contentVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.3 } }
  };

  const buttonVariants = {
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95, transition: { duration: 0.1 } }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={bannerVariants}
          className={`fixed bottom-0 left-0 right-0 z-50 ${className}`}
        >
          <div className="bg-gradient-to-r from-primary/95 to-primary shadow-lg border-t border-primary/20 p-4 md:p-6 backdrop-blur-sm">
            <div className="container mx-auto">
              <AnimatePresence mode="wait">
                {!showPreferences ? (
                  // Banner principale
                  <motion.div 
                    key="main-banner"
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={contentVariants}
                    className="flex flex-col md:flex-row items-center justify-between"
                  >
                    <div className="mb-6 md:mb-0 md:mr-8 flex items-start">
                      <div className="hidden md:block bg-white/20 p-3 rounded-full mr-4">
                        <FontAwesomeIcon icon={faCookieBite} className="text-white text-2xl" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2 text-white">La tua privacy è importante</h3>
                        <p className="text-white/90 text-sm md:text-base">
                          Utilizziamo i cookie per migliorare la tua esperienza sul nostro sito. Alcuni sono necessari, altri ci aiutano a personalizzare i contenuti.
                          <Link to="/privacy-policy" className="text-accent hover:underline ml-1 font-medium">
                            Privacy Policy
                          </Link>
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <motion.button
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                        onClick={() => setShowPreferences(true)}
                        className="px-5 py-2.5 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-colors text-white font-medium flex items-center justify-center"
                      >
                        <FontAwesomeIcon icon={faCog} className="mr-2" />
                        Personalizza
                      </motion.button>
                      <motion.button
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                        onClick={rejectAll}
                        className="px-5 py-2.5 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-colors text-white font-medium flex items-center justify-center"
                      >
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        Rifiuta
                      </motion.button>
                      <motion.button
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                        onClick={acceptAll}
                        className="px-5 py-2.5 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors font-medium flex items-center justify-center"
                      >
                        <FontAwesomeIcon icon={faCheck} className="mr-2" />
                        Accetta tutti
                      </motion.button>
                    </div>
                  </motion.div>
                ) : (
                  // Pannello preferenze
                  <motion.div
                    key="preferences"
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={contentVariants}
                  >
                    <h3 className="text-xl font-bold mb-4 text-white flex items-center">
                      <FontAwesomeIcon icon={faCookieBite} className="mr-2" /> 
                      Impostazioni Cookie
                    </h3>
                    <div className="space-y-4 mb-6">
                      {/* Cookie necessari */}
                      <div className="flex items-center justify-between p-4 bg-white/10 border border-white/20 rounded-lg backdrop-blur-sm">
                        <div className="flex items-start">
                          <div className="bg-white/20 p-2 rounded-full mr-3">
                            <FontAwesomeIcon icon={faShieldHalved} className="text-white" />
                          </div>
                          <div>
                            <h4 className="font-medium text-white">Cookie necessari</h4>
                            <p className="text-white/80 text-sm">Essenziali per il funzionamento del sito. Non possono essere disattivati.</p>
                          </div>
                        </div>
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={preferences.necessary}
                            disabled
                            className="sr-only"
                            id="necessary-cookies"
                          />
                          <label
                            htmlFor="necessary-cookies"
                            className="block w-14 h-7 rounded-full bg-accent transition-colors cursor-not-allowed"
                          >
                            <span className="block w-5 h-5 mt-1 ml-1 bg-white rounded-full transform translate-x-7 shadow-md"></span>
                          </label>
                        </div>
                      </div>
                      
                      {/* Cookie analitici */}
                      <div className="flex items-center justify-between p-4 bg-white/10 border border-white/20 rounded-lg backdrop-blur-sm">
                        <div className="flex items-start">
                          <div className="bg-white/20 p-2 rounded-full mr-3">
                            <FontAwesomeIcon icon={faChartSimple} className="text-white" />
                          </div>
                          <div>
                            <h4 className="font-medium text-white">Cookie analitici</h4>
                            <p className="text-white/80 text-sm">Ci aiutano a capire come utilizzi il sito per migliorarlo.</p>
                          </div>
                        </div>
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={preferences.analytics}
                            onChange={() => handlePreferenceChange('analytics')}
                            className="sr-only"
                            id="analytics-cookies"
                          />
                          <motion.label
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            htmlFor="analytics-cookies"
                            className={`block w-14 h-7 rounded-full transition-colors cursor-pointer ${
                              preferences.analytics ? 'bg-accent' : 'bg-white/30'
                            }`}
                          >
                            <motion.span
                              animate={{ 
                                translateX: preferences.analytics ? 28 : 4,
                              }}
                              transition={{ type: "spring", stiffness: 500, damping: 30 }}
                              className="block w-5 h-5 mt-1 ml-0 bg-white rounded-full shadow-md"
                            ></motion.span>
                          </motion.label>
                        </div>
                      </div>
                      
                      {/* Cookie di marketing */}
                      <div className="flex items-center justify-between p-4 bg-white/10 border border-white/20 rounded-lg backdrop-blur-sm">
                        <div className="flex items-start">
                          <div className="bg-white/20 p-2 rounded-full mr-3">
                            <FontAwesomeIcon icon={faBullhorn} className="text-white" />
                          </div>
                          <div>
                            <h4 className="font-medium text-white">Cookie di marketing</h4>
                            <p className="text-white/80 text-sm">Utilizzati per mostrarti annunci pertinenti sui nostri servizi.</p>
                          </div>
                        </div>
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={preferences.marketing}
                            onChange={() => handlePreferenceChange('marketing')}
                            className="sr-only"
                            id="marketing-cookies"
                          />
                          <motion.label
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            htmlFor="marketing-cookies"
                            className={`block w-14 h-7 rounded-full transition-colors cursor-pointer ${
                              preferences.marketing ? 'bg-accent' : 'bg-white/30'
                            }`}
                          >
                            <motion.span
                              animate={{ 
                                translateX: preferences.marketing ? 28 : 4,
                              }}
                              transition={{ type: "spring", stiffness: 500, damping: 30 }}
                              className="block w-5 h-5 mt-1 ml-0 bg-white rounded-full shadow-md"
                            ></motion.span>
                          </motion.label>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-3">
                      <motion.button
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                        onClick={() => setShowPreferences(false)}
                        className="px-5 py-2.5 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-colors text-white font-medium"
                      >
                        Indietro
                      </motion.button>
                      <motion.button
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                        onClick={savePreferences}
                        className="px-5 py-2.5 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors font-medium"
                      >
                        Salva preferenze
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner; 